import {PatientNotificationRequest, PatientNotificationStatus} from "@/core/http/resources/AppointmentResource";
import {Toast} from "@/stores/toast.store";
import {isValidFrenchMobile, normalizeFrenchMobile} from "@/agenda/lib/bookingLink";

/**
 * The counter's answer to « text the patient? » on a creation, a cancellation
 * or a move.
 * Unticked by default: nothing leaves unless the pharmacist asks for it.
 */
export interface PatientSmsChoice {
    notify: boolean;
    phone: string;
}

export const emptyPatientSmsChoice = (): PatientSmsChoice => ({notify: false, phone: ''});

/**
 * The mobile as the pharmacist reads it.
 *
 * The number comes as it was typed, so a French one is brought back to its
 * ten digits in pairs; anything else is shown as it came.
 */
export const phoneLabel = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');
    const national = /^(0033|33)\d{9}$/.test(digits) ? `0${digits.slice(-9)}` : digits;

    return /^0\d{9}$/.test(national) ? national.replace(/(\d{2})(?=\d)/g, '$1 ') : phone;
};

/** Ticked, the SMS needs a mobile to leave for. */
export const isPatientSmsChoiceValid = (choice: PatientSmsChoice): boolean => {
    return !choice.notify || isValidFrenchMobile(choice.phone);
};

export const patientSmsRequest = (choice: PatientSmsChoice | null): PatientNotificationRequest => {
    return choice?.notify
        ? {notify_patient: true, phone: normalizeFrenchMobile(choice.phone)}
        : {notify_patient: false};
};

const OUTCOMES: Record<PatientNotificationStatus, Pick<Toast, 'type' | 'content'>> = {
    sent: {type: 'success', content: 'Le SMS a été envoyé au patient.'},
    no_phone: {type: 'warning', content: 'Aucun SMS envoyé : pas de mobile pour ce patient.'},
    no_consent: {type: 'info', content: 'Aucun SMS envoyé : le patient a désactivé les notifications de rendez-vous.'}
};

/**
 * The toast telling the counter what became of the SMS it asked for. None
 * when it asked for nothing, or when the server had nothing to announce (the
 * appointment did not actually move).
 */
export const patientSmsOutcome = (
    choice: PatientSmsChoice | null,
    status: PatientNotificationStatus | null
): Pick<Toast, 'type' | 'content'> | null => {
    if (!choice?.notify || !status) {
        return null;
    }

    return OUTCOMES[status] ?? null;
};
